import { useMemo } from 'react';
import { useErrorRenderer } from './useErrorRenderer';
import { ReformReturn } from '../../../types';
import { FieldPath } from '../../form/form-groups';

/**
 * Hook for managing custom error renderers in Reform forms
 *
 * @template T - The type of form data
 * @param reform - The Reform hook return value
 * @returns Object with error renderer utilities
 *
 * @example
 * // Basic usage
 * const reform = useReform(config);
 * const errorRenderer = useReformErrorRenderer(reform);
 *
 * // Register a custom renderer for email fields
 * useEffect(() => {
 *   const unregister = errorRenderer.registerErrorRenderer({
 *     field: 'email',
 *     renderer: ({ error }) => <EmailErrorMessage error={error} />
 *   });
 *
 *   return unregister;
 * }, []);
 *
 * // Use the renderer in your form
 * return (
 *   <div>
 *     <input {...reform.register(0, 'email')} />
 *     {errorRenderer.renderFieldError(0, 'email')}
 *   </div>
 * );
 */
export const useReformErrorRenderer = <T extends Record<string, any>>(
  reform: ReformReturn<T>
) => {
  const groups = reform.getGroups();
  const errorRenderer = useErrorRenderer<T>(groups);

  // Enhanced API that integrates with Reform
  return useMemo(
    () => ({
      ...errorRenderer,

      /**
       * Render an error for a specific field using Reform's error state
       *
       * @param groupIndex - The group index
       * @param field - The field path
       * @returns The rendered error
       */
      renderFieldError: (groupIndex: number, field: FieldPath<T>) => {
        const error = reform.getFieldError(groupIndex, field) as string;
        return errorRenderer.renderError(field, error, groupIndex);
      },

      /**
       * Render all errors for a group
       *
       * @param groupIndex - The group index
       * @returns Array of rendered errors
       */
      renderGroupErrors: (groupIndex: number) => {
        const errors = reform.getGroupErrors(groupIndex);
        return Object.entries(errors).map(([field, error]) =>
          errorRenderer.renderError(field as FieldPath<T>, error, groupIndex)
        );
      },
    }),
    [errorRenderer, reform]
  );
};
